import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  CloudArrowUpIcon,
  DocumentDuplicateIcon,
  ServerIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import moment from "moment"
import React, { useEffect, useState } from "react"
import DropDownSelect from "../DropdownSelect"
import LoadingOverlay from "aldoo-ra/LoadingOverlay"
import RestoreDialog from "./restore-dialog"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import {
  ListBackups,
  UploadBackup,
  DeleteBackup,
  CreateBackup,
  RestoreBackup,
  DownloadBackup,
  ReadGlobal,
} from "./api"
import { showNotification } from "../Notification"
import { usePromise } from "aldoo-ra/usePromise"
import Typography from "../Typography"
import Tooltip from "aldoo-ra/Tooltip"

export default function BackupBrowser() {
  const [loading, setLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedBackup, setSelectedBackup] = useState(null)
  const [tableTitle, setTableTitle] = useState("")
  const [totalBackupSize, setTotalBackupSize] = useState("0")
  const [lastBackup, setLastBackup] = useState("")
  const [nextBackupTime, setNextBackupTime] = useState("")
  const [backupSource, setBackupSource] = useState([
    {
      label: "Local",
      value: "local",
    },
  ])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [activityMessage, setActivityMessage] = useState("")
  const [restoreProgress, setRestoreProgress] = useState(0)
  const [restoreLog, setRestoreLog] = useState([])
  const [restoreActivities, setRestoreActivities] = useState([])
  const [currentActivity, setCurrentActivity] = useState("")
  const [restoreStatus, setRestoreStatus] = useState("")
  const [overlayTitle, setOverlayTitle] = useState(
    "Please wait while we load the data"
  )

  useEffect(() => {
    //set the table title
    setTableTitle(
      (backupSource && backupSource[0]?.value === "local") || !backupSource
        ? "Local Backups"
        : "Remote Backups"
    )
  }, [backupSource])

  usePromise(async () => {
    //get the next backup time
    const response = await ReadGlobal("next-run-backup")
    if (!response || response.error) return
    setNextBackupTime(response)
  })

  const GetBackups = async (settings) => {
    let value = backupSource && backupSource[0]?.value
    const script =
      value === "local" || !value ? "backup-list-local" : "backup-list-remote"

    setOverlayTitle("Loading backups")
    setActivityMessage("") // Clear any previous activity message
    setLoading(true)
    const response = await ListBackups({ script })

    setTotalBackupSize(response.result.formattedSize)

    setLoading(false)

    if (settings.sort) {
      //sort the files based on the sort field
      response.result.files = response.result.files.sort((a, b) => {
        if (settings.sort.timeCreated === 1) {
          return new Date(a.timeCreated) - new Date(b.timeCreated)
        } else {
          return new Date(b.timeCreated) - new Date(a.timeCreated)
        }
      })
    }

    setLastBackup(
      //get the last backup date
      response.result.files.length > 0
        ? moment(response.result.files[0].timeCreated).fromNow()
        : "No backups available"
    )

    if (settings.search && settings.search.path) {
      //filter the files based on the search field
      response.result.files = response.result.files.filter((item) =>
        item.path.includes(settings.search.path)
      )
    }

    const total = response.result.files.length
    if (settings.page && settings.itemsPerPage) {
      //paginate the files
      const start = (settings.page - 1) * settings.itemsPerPage
      const end = start + settings.itemsPerPage
      response.result.files = response.result.files.slice(start, end)
    }

    return {
      data: response.result.files,
      total,
    }
  }

  const handleUploadBackup = () => {
    //show open file dialog
    const input = document.createElement("input")
    input.type = "file"
    input.accept = ".7z"
    input.onchange = async (e) => {
      const file = e.target.files[0]
      setOverlayTitle("Uploading backup")
      setLoading(true)
      const response = await UploadBackup({
        file,
        setProgress: setUploadProgress,
      })
      setLoading(false)

      if (!response.error) {
        showNotification({
          message: "Backup uploaded successfully",
          className: "bg-admin_primary text-admin_text_inv",
        })
      }

      setRefreshKey((prev) => prev + 1)

      //Remove the input element
      input.remove()
    }
    input.click()
  }

  const handleDeleteBackup = async (item) => {
    showPromptDialog({
      title: "Delete Backup",
      message: (
        <div className="flex flex-col text-center">
          <span className="mb-5 text-text font-semibold">
            {item.path.split("/").pop()}
          </span>
          <span className="text-primary">
            Are you sure you want to delete the backup?
          </span>
          <br />
          <span className="text-admin_neutral_2 ">This action cannot be undone.</span>
        </div>
      ),
      onConfirm: async () => {
        const value = backupSource && backupSource[0]?.value
        const isLocal = value === "local" || !value
        const script = isLocal ? "backup-delete-local" : "backup-delete-remote"

        // For local: extract filename, for remote: use full path from GCS
        const file = isLocal ? item.path.split("/").pop() : item.path

        const response = await DeleteBackup({
          script,
          env: { FILE: file },
        })

        if (!response.error) {
          showNotification({
            message: "Backup deleted successfully",
            className: "bg-admin_primary text-admin_text_inv",
          })
        }

        setTimeout(() => {
          setRefreshKey((prev) => prev + 1)
        }, 500)
      },
    })
  }

  const handleDownloadBackup = async (item) => {
    let value = backupSource && backupSource[0]?.value
    const isLocal = value === "local" || !value

    if (!isLocal) {
      setOverlayTitle("Downloading backup")
      setLoading(true)
    }

    const BACKUP_IMAGE_FILE = isLocal ? item.path : item.path.split("/").pop()
    const response = await DownloadBackup(BACKUP_IMAGE_FILE, isLocal)

    if (response.error) {
      showNotification({
        message: response.error,
        className: "bg-error text-admin_text_inv",
      })
      setLoading(false)
      return
    }

    const downloadLink = document.createElement("a")
    downloadLink.href = response.download
    downloadLink.download = response.filename
    downloadLink.click()

    showNotification({
      message: "Backup downloaded successfully",
      className: "bg-admin_primary text-admin_text_inv",
    })
    setLoading(false)
  }

  const handleBackup = async () => {
    if (loading) return
    setOverlayTitle("Creating backup")
    setActivityMessage("") // Clear any previous activity message
    setLoading(true)
    await CreateBackup({
      run: { script: "backup-local" },
      onResponse: (response) => {
        setActivityMessage(response.activity)
      },
    })
    setLoading(false)

    showNotification({
      message: "Backup completed successfully",
      className: "bg-admin_primary text-admin_text_inv",
    })

    setRefreshKey((prev) => prev + 1)
  }

  const handleRestore = (backup, restoreOptions) => {
    showPromptDialog({
      title: "Restore Backup",
      message: (
        <div className="flex flex-col text-center">
          <span className="mb-5 text-text font-semibold">
            {backup.path.split("/").pop()}
          </span>
          <span className="text-primary">
            Are you sure you want to restore the site state from this backup?
          </span>
        </div>
      ),
      onConfirm: async () => {
        let value = backupSource && backupSource[0]?.value
        const isLocal = value === "local" || !value

        const BACKUP_IMAGE_FILE = isLocal
          ? backup.path
          : backup.path.split("/").pop()

        setLoading(true)
        setOverlayTitle("Restoring backup")
        
        // Reset all restore states when starting a new restore
        setRestoreProgress(0)
        setRestoreLog([])
        setRestoreActivities([])
        setRestoreStatus("running")
        setCurrentActivity("")
        setActivityMessage("")

        const {
          DB_RESTORE_MODE,
          INCLUDE_STATIC_FILES,
          INCLUDE_DATABASE,
          INCLUDE_DEPLOYMENT,
        } = restoreOptions

        const result = await RestoreBackup({
          run: {
            script: "restore-local",
            env: {
              BACKUP_IMAGE_FILE,
              IS_REMOTE_BACKUP: !isLocal,
              DB_RESTORE_MODE,
              INCLUDE_STATIC_FILES,
              INCLUDE_DATABASE,
              INCLUDE_DEPLOYMENT,
            },
          },
          onResponse: (response) => {
            // Track current status and activity
            const status = response.status || "running"
            const activity = response.activity || "Processing..."
            const progress = response.progress || 0
            
            setRestoreStatus(status)
            setCurrentActivity(activity)
            setRestoreProgress(progress)
            
            
            // Add activity to activities list if it's new and not empty
            if (activity && activity.trim()) {
              setRestoreActivities(prev => {
                // Only add if it's different from the last activity
                if (prev.length === 0 || prev[prev.length - 1].activity !== activity) {
                  return [...prev, {
                    activity,
                    timestamp: new Date().toLocaleTimeString(),
                    progress,
                    status,
                    completed: status === "completed"
                  }]
                } else if (prev.length > 0) {
                  // Update the last activity's progress and status
                  const updated = [...prev]
                  updated[updated.length - 1] = {
                    ...updated[updated.length - 1],
                    progress,
                    status,
                    completed: status === "completed"
                  }
                  return updated
                }
                return prev
              })
            }
            
            // Enhanced activity message with statistics
            let enhancedActivity = activity
            
            // Add collection progress if available
            if (response.result?.completedCollections !== undefined && response.result?.totalCollections) {
              const completed = response.result.completedCollections
              const total = response.result.totalCollections
              const current = response.result.currentCollection
              enhancedActivity += `\n\nDatabase Collections: ${completed}/${total} completed`
              if (current) {
                enhancedActivity += `\nCurrent: ${current}`
              }
            }
            
            // Add file statistics if available
            if (response.result?.fileCount !== undefined || response.result?.totalSize !== undefined) {
              const fileCount = response.result.fileCount || 0
              const totalSize = response.result.totalSize || 0
              const sizeInMB = totalSize > 0 ? (totalSize / 1024 / 1024).toFixed(2) : '0'
              enhancedActivity += `\n\nFiles: ${fileCount} processed (${sizeInMB} MB)`
            }
            
            // Don't add overall progress to message since it's shown in progress bar
            setActivityMessage(enhancedActivity)
            
            // Update restore log
            if (response.log && response.log.length > 0) {
              setRestoreLog(response.log)
            }
          },
        })
        
        // Don't set loading to false if the restore completed successfully
        // We want to keep the dialog open for completion acknowledgment
        if (result.status !== "completed" || result.error) {
          setLoading(false)
        }

        // Check for errors in the result or task status
        if (result.error || (result.status && (result.status === "failed" || result.status === "error"))) {
          const errorMessage = result.error || "Restore operation failed"
          const taskName = result.name || "Unknown Task"
          const taskId = result.id || "N/A"
          const currentActivity = result.activity || "Unknown activity"
          const progress = result.progress || 0
          const taskLog = result.log || []
          
          const ErrorDialogContent = () => {
            const [showDetails, setShowDetails] = React.useState(false)
            
            return (
              <div className="flex flex-col space-y-4">
                <div className="text-center">
                  <div className="text-error font-semibold text-lg mb-2">
                    {errorMessage}
                  </div>
                </div>
                
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center gap-2 text-admin_primary hover:text-admin_primary_dk transition-colors text-sm"
                  >
                    <span>See Details</span>
                    {showDetails ? (
                      <ChevronUpIcon className="w-4 h-4" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
                
                {showDetails && (
                  <div className="bg-admin_canvas p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-admin_text_secondary">Task:</span>
                        <div className="text-admin_text font-mono">{taskName}</div>
                      </div>
                      <div>
                        <span className="text-admin_text_secondary">Progress:</span>
                        <div className="text-admin_text">{progress}%</div>
                      </div>
                      <div className="col-span-2">
                        <span className="text-admin_text_secondary">Last Activity:</span>
                        <div className="text-admin_text">{currentActivity}</div>
                      </div>
                      <div className="col-span-2">
                        <span className="text-admin_text_secondary">Task ID:</span>
                        <div className="text-admin_text font-mono text-xs">{taskId}</div>
                      </div>
                    </div>
                    
                    {taskLog.length > 0 && (
                      <div className="mt-4">
                        <span className="text-admin_text_secondary text-sm">Task Log:</span>
                        <div className="mt-2 bg-admin_paper p-3 rounded border max-h-32 overflow-y-auto">
                          {taskLog.map((logEntry, index) => (
                            <div key={index} className="text-xs font-mono text-admin_text mb-1 last:mb-0">
                              {logEntry}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          }
          
          showPromptDialog({
            title: "Restore Failed",
            message: <ErrorDialogContent />,
            onConfirm: () => {},
            confirmText: "OK",
            showNoBtn: false,
          })
        } else {
          // Mark all activities as completed
          setRestoreActivities(prev => 
            prev.map(activity => ({
              ...activity,
              completed: true
            }))
          )
          // Keep loading as false, but restoreStatus will be "completed" so dialog shows completion state
        }

        setRefreshKey((prev) => prev + 1)
      },
    })
  }

  const headerControls = (
    <div className="w-full mt-3">
      <DropDownSelect
        variant="admin"
        options={[
          { label: "Local", value: "local" },
          { label: "Remote", value: "remote" },
        ]}
        fixedWidth="170px"
        value={backupSource}
        onChange={(value) => {
          setBackupSource(value)
          setRefreshKey((prev) => prev + 1)
        }}
      />
    </div>
  )

  const tableItemRender = [
    {
      id: "path",
      title: "Archive Name",
      headerClassName: "w-[400px]",
      render: (value, item) => (
        <div className="flex items-center">
          <ServerIcon className="w-5 h-5 mr-2" />
          <Typography
            variant="admin_txt1" 
            className="text-admin_text"
          >
            {item.path.split("/").pop()}
          </Typography>
        </div>
      ),
    },
    {
      id: "timeCreated",
      title: "Date & Time",
      headerClassName: "w-[200px]",
      render: (value, item) => (
        <div className="flex flex-col gap-1">
          <Typography 
            variant="admin_txt1"
          >
            {moment(item.timeCreated).format("YYYY-MM-DD | HH:mm")}
          </Typography>
          <Typography 
            variant="admin_txt1_dim"
          >
            {moment(item.timeCreated).fromNow()}
          </Typography>
        </div>
      ),
    },
    {
      id: "size",
      title: "Size",
      headerClassName: "w-[150px]",
      render: (value, item) => (
        <Typography 
            variant="admin_txt1"
          >
          {item.formattedSize}
        </Typography>
      ),
    },
    {
      id: "restore",
      headerClassName: "w-8",
      render: (value, item) => (
        <Tooltip
          text="Restore"
        >
          <Button
            variant="admin_icon"
            onClick={() => {
              setSelectedBackup(item)
            }}
          >
            <ArrowPathIcon className="w-4 h-4" />
          </Button>
        </Tooltip>
      ),
    },
    {
      id: "download",
      headerClassName: "w-8",
      render: (value, item) => (
        <Tooltip
          text="Download"
        >
          <Button
            variant="admin_icon"
            onClick={() => {
              handleDownloadBackup(item)
            }}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
          </Button>
        </Tooltip>
      ),
    },
    {
      id: "delete",
      headerClassName: "w-8",
      render: (value, item) => (
        <Tooltip
          text="Delete"
        >
          <Button
            variant="admin_icon"
            onClick={() => {
              handleDeleteBackup(item)
            }}
          >
            <XMarkIcon className="w-4 h-4" />
          </Button>
        </Tooltip>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-admin_canvas p-4">
      <div className="max-w-7xl mx-auto">
        <LoadingOverlay
          title={overlayTitle}
          message={activityMessage}
          loading={loading}
          progress={(overlayTitle === "Restoring backup" || restoreStatus === "completed") ? restoreProgress : undefined}
          log={(overlayTitle === "Restoring backup" || restoreStatus === "completed") ? restoreLog : undefined}
          activities={(overlayTitle === "Restoring backup" || restoreStatus === "completed") ? restoreActivities : undefined}
          currentStatus={(overlayTitle === "Restoring backup" || restoreStatus === "completed") ? restoreStatus : undefined}
          onComplete={() => {
            // Reset states when user acknowledges completion
            setLoading(false) // Now we can set loading to false
            setRestoreProgress(0)
            setRestoreLog([])
            setRestoreActivities([])
            setRestoreStatus("")
            setCurrentActivity("")
            setActivityMessage("")
          }}
        />

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <Typography 
              component="h1"
              variant="admin_h2"
            >
              Backup Management
            </Typography>

            <Typography 
              variant="admin_body_dim"
              className="mt-2 mb-0"
            >
              Manage and monitor your site backups
            </Typography>
          </div>

          <div className="flex gap-4">
            <Button
              variant="admin_secondary"
              onClick={handleUploadBackup}
              className="gap-2"
            >
              <CloudArrowUpIcon className="w-6 h-6" />
              Upload
            </Button>
            
            <Button
              variant="admin_primary"
              onClick={handleBackup}
              className="flex flex-row gap-2"
            >
              Backup Now
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* First Card */}
          <div className="flex items-center justify-between bg-admin_paper rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-full flex items-start justify-between">
              <div>
                <Typography
                  variant="admin_h4"
                  className="mb-1"
                >
                  Last Backup
                </Typography>
                <Typography 
                  variant="admin_body"
                  className="text-[18px] mb-0"
                >
                  {lastBackup}
                </Typography>
              </div>
              <CheckCircleIcon className="w-10 h-10 text-admin_success" />
            </div>
          </div>

          {/* Second Card */}
          <div className="flex items-center justify-between bg-admin_paper rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-full flex items-start justify-between">
              <div>
                <Typography
                  variant="admin_h4"
                  className="mb-1"
                >
                  {(backupSource && backupSource[0]?.value === "local") ||
                  !backupSource
                    ? "Total Local Backup Size"
                    : "Total Remote Backup Size"}
                </Typography>
                <Typography 
                  variant="admin_body"
                  className="text-[18px] mb-0"
                >
                  {totalBackupSize}
                </Typography>
              </div>
              <DocumentDuplicateIcon className="w-10 h-10 text-admin_primary" />
            </div>
          </div>

          {/* Third Card */}
          <div className="flex items-center justify-between bg-admin_paper rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            {nextBackupTime && (
              <div className="w-full flex items-start justify-between">
                <div>
                  <Typography
                    variant="admin_h4"
                    className="mb-1"
                  >
                    {`Next Auto Backup ${moment(nextBackupTime).fromNow()}`}
                  </Typography>
                  <Typography 
                    variant="admin_body"
                    className="text-[18px] mb-0"
                  >
                    {moment(nextBackupTime).format("YYYY-MM-DD | HH:mm") + " UTC"}
                  </Typography>
                </div>
                <ClockIcon className="w-10 h-10 text-purple-500" />
              </div>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-admin_paper rounded-3xl shadow-sm">
          <div className="py-6">
            <DataTable
              variant="admin_browser"
              title={tableTitle}
              refreshKey={refreshKey}
              apiMethod={GetBackups}
              preferredHeight="60vh"
              headerControls={headerControls}
              itemHeight="h-[70px]"
              searchField="path"
              requestFields={{ match: { deleted: { $ne: true } } }}
              searchObject={{}}
              sort={{ timeCreated: -1 }}
              localUserPreferencesKey="backups.browser"
              renders={tableItemRender}
              noData={
                <div className="text-xl text-admin_text">
                  No backups available
                </div>
              }
            />
          </div>
        </div>

        {selectedBackup && (
          <RestoreDialog
            selectedBackup={selectedBackup}
            onRestore={handleRestore}
            onClose={() => setSelectedBackup(null)}
          />
        )}
      </div>
    </div>
  )
}